import { create } from 'zustand';
import {
  sessionService,
  type GameSession as AntiCheatSession,
} from '../services/session.service';

/**
 * Game session state
 */
export interface GameSession {
  gameId: string;
  startedAt: number;
  score: number;
  level: number;
  lives: number;
  isPaused: boolean;
  isGameOver: boolean;
  // Anti-cheat session tracking
  antiCheatSessionId?: string;
}

/**
 * Game store state
 */
interface GameStoreState {
  // Current session
  currentSession: GameSession | null;

  // History of recent sessions (for stats)
  recentSessions: GameSession[];

  // Actions
  startGame: (gameId: string, initialLives?: number) => void;
  endGame: () => void;
  updateScore: (score: number) => void;
  addScore: (points: number) => void;
  setLevel: (level: number) => void;
  levelUp: () => void;
  loseLife: () => void;
  setPaused: (isPaused: boolean) => void;
  togglePause: () => void;
  setGameOver: () => void;
  resetSession: () => void;
}

const MAX_RECENT_SESSIONS = 10;

export const useGameStore = create<GameStoreState>((set, get) => ({
  currentSession: null,
  recentSessions: [],

  startGame: (gameId, initialLives = 3) => {
    // Start anti-cheat session asynchronously
    sessionService
      .start(gameId)
      .then((antiCheatSession: AntiCheatSession) => {
        const { currentSession } = get();
        if (currentSession && currentSession.gameId === gameId) {
          set({
            currentSession: {
              ...currentSession,
              antiCheatSessionId: antiCheatSession.sessionId,
            },
          });
        }
      })
      .catch((err: Error) => {
        console.warn('Failed to start anti-cheat session:', err);
      });

    set({
      currentSession: {
        gameId,
        startedAt: Date.now(),
        score: 0,
        level: 1,
        lives: initialLives,
        isPaused: false,
        isGameOver: false,
      },
    });
  },

  endGame: () => {
    const { currentSession, recentSessions } = get();
    if (currentSession) {
      // End anti-cheat session if still active
      if (!currentSession.isGameOver) {
        sessionService.end(currentSession.score).catch((err: Error) => {
          console.warn('Failed to end anti-cheat session:', err);
        });
      }

      // Add to recent sessions
      const updated = [currentSession, ...recentSessions].slice(
        0,
        MAX_RECENT_SESSIONS
      );
      set({
        currentSession: null,
        recentSessions: updated,
      });
    }
  },

  updateScore: (score) => {
    const { currentSession } = get();
    if (currentSession) {
      set({
        currentSession: { ...currentSession, score },
      });
    }
  },

  addScore: (points) => {
    const { currentSession } = get();
    if (currentSession) {
      set({
        currentSession: {
          ...currentSession,
          score: currentSession.score + points,
        },
      });
    }
  },

  setLevel: (level) => {
    const { currentSession } = get();
    if (currentSession) {
      set({
        currentSession: { ...currentSession, level },
      });
    }
  },

  levelUp: () => {
    const { currentSession } = get();
    if (currentSession) {
      set({
        currentSession: {
          ...currentSession,
          level: currentSession.level + 1,
        },
      });
    }
  },

  loseLife: () => {
    const { currentSession } = get();
    if (currentSession && currentSession.lives > 0) {
      const newLives = currentSession.lives - 1;
      set({
        currentSession: {
          ...currentSession,
          lives: newLives,
          isGameOver: newLives <= 0,
        },
      });
    }
  },

  setPaused: (isPaused) => {
    const { currentSession } = get();
    if (currentSession && !currentSession.isGameOver) {
      set({
        currentSession: { ...currentSession, isPaused },
      });
    }
  },

  togglePause: () => {
    const { currentSession } = get();
    if (currentSession && !currentSession.isGameOver) {
      set({
        currentSession: {
          ...currentSession,
          isPaused: !currentSession.isPaused,
        },
      });
    }
  },

  setGameOver: () => {
    const { currentSession } = get();
    if (currentSession) {
      // End anti-cheat session with final score
      sessionService.end(currentSession.score).catch((err: Error) => {
        console.warn('Failed to end anti-cheat session:', err);
      });

      set({
        currentSession: { ...currentSession, isGameOver: true },
      });
    }
  },

  resetSession: () => {
    set({ currentSession: null });
  },
}));

/**
 * Hook for game state with convenient helpers
 */
export function useGame() {
  const store = useGameStore();

  return {
    // State
    session: store.currentSession,
    isPlaying:
      store.currentSession !== null && !store.currentSession.isGameOver,
    isPaused: store.currentSession?.isPaused || false,
    isGameOver: store.currentSession?.isGameOver || false,
    score: store.currentSession?.score || 0,
    level: store.currentSession?.level || 1,
    lives: store.currentSession?.lives || 0,
    recentSessions: store.recentSessions,
    // Anti-cheat session ID (for score submission)
    sessionId: store.currentSession?.antiCheatSessionId || null,

    // Actions
    startGame: store.startGame,
    endGame: store.endGame,
    updateScore: store.updateScore,
    addScore: store.addScore,
    setLevel: store.setLevel,
    levelUp: store.levelUp,
    loseLife: store.loseLife,
    setPaused: store.setPaused,
    togglePause: store.togglePause,
    setGameOver: store.setGameOver,
    resetSession: store.resetSession,
  };
}

/**
 * Get stats from recent sessions
 */
export function getSessionStats(sessions: GameSession[]): {
  totalGames: number;
  totalScore: number;
  highScore: number;
  averageScore: number;
  totalPlayTime: number;
} {
  if (sessions.length === 0) {
    return {
      totalGames: 0,
      totalScore: 0,
      highScore: 0,
      averageScore: 0,
      totalPlayTime: 0,
    };
  }

  const totalScore = sessions.reduce((sum, s) => sum + s.score, 0);
  const highScore = Math.max(...sessions.map((s) => s.score));

  return {
    totalGames: sessions.length,
    totalScore,
    highScore,
    averageScore: Math.round(totalScore / sessions.length),
    totalPlayTime: 0, // Would need endedAt timestamp to calculate
  };
}
